import { Fragment } from 'react'
import { useMutation } from '@tanstack/react-query'
import { FormattedMessage, useIntl } from 'react-intl'
import { toast } from 'sonner'

import { DEV_OTP } from '@/constants/otp'
import { BASE_URL_DEV } from '@/constants/urls'
import { useBusinessUser } from '@/domains/Business/hooks'
import { useErrorToast } from '@/hooks/useErrorToast'
import { getImage } from '@/lib/images'
import { isProduction } from '@/lib/utils'
import { Button, Card, Typography } from '@/shared/ui'

import { sendContractorReminder } from '../../api'
import { ContractorAgreementType, ContractorDetails } from '../../types'

const Wrapper = ({
  children,
  variant,
}: {
  children: React.ReactNode
  variant: 'sidebar' | 'widget'
}) => {
  return variant === 'sidebar' ? (
    <Card className="max-w-full" size="medium">
      {children}
    </Card>
  ) : (
    <Fragment>{children}</Fragment>
  )
}

type Props = {
  contractor?: ContractorDetails
  variant?: 'sidebar' | 'widget'
}

export const SendReminderToDo = ({ contractor, variant = 'widget' }: Props) => {
  const intl = useIntl()
  const notifyError = useErrorToast()
  const businessUser = useBusinessUser()

  const { mutateAsync, isPending } = useMutation({
    mutationFn: sendContractorReminder,
  })

  const latestContract = contractor?.contracts[0]

  const onSendReminder = async () => {
    if (!contractor?.id) {
      return
    }

    try {
      await mutateAsync({ id: contractor.id })

      toast.success(
        intl.formatMessage({
          id: 'team.member.reminder.success',
          defaultMessage: 'Invitation reminder sent successfully',
        }),
      )

      if (!isProduction()) {
        window.open(
          `${BASE_URL_DEV}/contractor/accept-invite?identity=${contractor.identityId}&business=${businessUser?.businessId}&otp=${DEV_OTP}`,
          '_blank',
        )
      }
    } catch (error) {
      notifyError(error)
    }
  }

  return (
    <Wrapper variant={variant}>
      <div className="flex flex-wrap items-center justify-between gap-8 md:flex-nowrap md:gap-0">
        <div className="flex flex-wrap gap-8 md:flex-nowrap md:gap-3">
          {variant === 'sidebar' ? null : (
            <img
              className="hidden w-8 object-contain md:flex"
              src={getImage({ name: 'todo-list', category: 'contractors' })}
              alt=""
              aria-hidden
            />
          )}
          <div className="flex flex-col justify-center">
            <Typography bold>
              {(() => {
                switch (latestContract?.agreementType) {
                  case ContractorAgreementType.STANDARD:
                    return (
                      <FormattedMessage
                        id="contractors.toDo.signContract.standard.title"
                        defaultMessage="You have invited {name} to sign their contract"
                        values={{ name: contractor?.personalDetails.firstName }}
                      />
                    )

                  case ContractorAgreementType.CUSTOM:
                    return (
                      <FormattedMessage
                        id="contractors.toDo.signContract.custom.title"
                        defaultMessage="You have invited {name} to set up their account and payment method"
                        values={{ name: contractor?.personalDetails.firstName }}
                      />
                    )

                  default:
                    return null
                }
              })()}
            </Typography>
            <Typography variant="body-small" className="text-neutral-gray-600">
              {(() => {
                switch (latestContract?.agreementType) {
                  case ContractorAgreementType.STANDARD:
                    return (
                      <FormattedMessage
                        id="contractors.toDo.signContract.standard.description"
                        defaultMessage="Signature is pending. Give them a friendly reminder so they finalize the onboarding"
                      />
                    )

                  case ContractorAgreementType.CUSTOM:
                    return (
                      <FormattedMessage
                        id="contractors.toDo.signContract.custom.description"
                        defaultMessage="The process is incomplete. Give them a friendly reminder so they finalize the onboarding"
                      />
                    )

                  default:
                    return null
                }
              })()}
            </Typography>
          </div>
        </div>

        <Button
          onClick={onSendReminder}
          loading={isPending}
          disabled={isPending}
        >
          <FormattedMessage id="actions.remind" defaultMessage="Remind" />
        </Button>
      </div>
    </Wrapper>
  )
}
